<template>
  <div class="request-info">
    <div v-if="showShipments && isMCFOrder">
      <h4 class="expanded-title">
        Shipment/Fulfilment Details
      </h4>
      <el-table
          v-loading="loading"
          :data="shipments"
          style="width: 100%">
        <el-table-column
            label="Item Description"
            prop="item_description"
            width="200" />
        <el-table-column
            label="Quantity"
            prop="quantity"
            width="200" />
        <el-table-column
            label="Ship Date"
            prop="shipping_datetime">
          <template slot-scope="scope">
            <time-zone-formatter :current-time="scope.row.shipping_datetime" />
          </template>
        </el-table-column>
        <el-table-column
            label="Estimated Arrival"
            prop="arrival_datetime">
          <template slot-scope="scope">
            <time-zone-formatter :current-time="scope.row.arrival_datetime" />
          </template>
        </el-table-column>
        <el-table-column
            label="Tracking Number"
            prop="tracking_id" />
        <el-table-column
            label="Shipment Status"
            prop="shipment_status" />
      </el-table>
    </div>

    <h4 class="expanded-title">
      Request Details
      <a
          style="float: right"
          @click="copyData()">Copy Request
      </a>
    </h4>
    <customer-request-copy-data
        ref="copyRequestModal"
        :request="request" />
    <el-row :gutter="40">
      <el-col :span="12">
        <h4>Reasons/Parts</h4>
        <el-table
            :data="request.reasons"
            style="width: 100%">
          <el-table-column
              label="Code"
              prop="code"
              width="100" />
          <el-table-column
              label="Reason"
              prop="reason" />
          <el-table-column
              v-if="request.type !== 'part'">
            <template slot-scope="scope">
              <el-tag
                  v-if="request.has_return_label"
                  type="warning"
                  size="mini">
                Return label
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column
              v-if="request.type === 'part'"
              width="100"
              label="Quantity"
              prop="quantity" />
          <el-table-column
              v-if="request.type === 'part'"
              width="150"
              label="SKU"
              prop="sku" />
        </el-table>
      </el-col>
      <el-col :span="6">
        <h4>Customer Address</h4>
        <div class="request-address-row">
          <b>Address:</b>
          <span>{{ address.address_1 }}</span>
        </div>
        <div class="request-address-row">
          <b>Address 2:</b>
          <span>{{ address.address_2 }}</span>
        </div>
        <div class="request-address-row">
          <b>City:</b>
          <span>{{ address.city | capitalize }}</span>
        </div>
        <div class="request-address-row">
          <b>State:</b>
          <span>{{ address.state | uppercase }}</span>
        </div>
        <div class="request-address-row">
          <b>Zip:</b>
          <span>{{ address.zip | capitalize }}</span>
        </div>
        <div class="request-address-row">
          <b>Country:</b>
          <span>{{ address.country | capitalize }}</span>
        </div>
        <div class="request-address-row">
          <b>Phone:</b>
          <span>{{ address.phone }}</span>
        </div>
      </el-col>
      <el-col :span="6">
        <h4>Shipping</h4>
        <div class="request-address-row">
          <b>Market:</b>
          <span>{{ address.market | capitalize }}</span>
        </div>
        <div class="request-address-row">
          <b>GID:</b>
          <span>{{ request.gid || 'N/A' }}</span>
        </div>
        <div
            v-if="request.owned_sku"
            class="request-address-row">
          <b>Original SKU:</b>
          <span>{{ request.owned_sku | uppercase }}</span>
        </div>
        <div
            v-if="request.type === 'replacement' && request.sku"
            class="request-address-row">
          <b>Shipped SKU:</b>
          <span>{{ request.sku | uppercase }}</span>
        </div>
        <div
            v-if="request.type === 'replacement'"
            class="request-address-row">
          <b>Replacement ID:</b>
          <span>{{ request.new_device_id }}</span>
        </div>
        <div
            v-if="request.type === 'replacement'"
            class="request-address-row">
          <b>Stock:</b>
          <span>{{ request.stock | uppercase }}</span>
        </div>
        <div
            v-if="request.kustomer_url"
            class="request-address-row">
          <b>Kustomer URL:</b>
          <a
              v-if="isKustomerURL(request.kustomer_url)"
              :href="request.kustomer_url"
              target="_blank">Kustomer Link</a>
          <el-tooltip
              v-else
              content="The url entered in the order is invalid">
            <span style="word-break: break-all;padding-left: 8px;">{{ request.kustomer_url }}</span>
          </el-tooltip>
        </div>
      </el-col>
    </el-row>
    <div v-if="enableReturnLabels">
      <h4 class="expanded-title flex">
        Return Labels
        <el-tooltip
            :content="generateLabelTooltip"
            :disabled="canGenerateLabel"
            placement="top">
          <el-popover
              v-model="visible"
              placement="left"
              width="300"
              :disabled="!canGenerateLabel">
            <h5>Custom Email form</h5>
            <div style="margin: 10px 0px;">
              <el-input
                  v-model="customEmail"
                  size="small"
                  placeholder="Email" />
              <div style="color: #999;text-align: center;font-size: 12px;margin: 10px;">
                Leave this email empty to send the label to the <br> email set in the request
              </div>
            </div>
            <div style="text-align: right; margin: 0">
              <el-button
                  size="mini"
                  type="text"
                  @click="visible = false">
                cancel
              </el-button>
              <el-button
                  type="primary"
                  size="mini"
                  :disabled="!validEmail"
                  @click="generateLabel()">
                Confirm
              </el-button>
            </div>
            <el-button
                slot="reference"
                type="warning"
                size="small"
                :disabled="!canGenerateLabel">
              Generate label
            </el-button>
          </el-popover>
        </el-tooltip>
      </h4>
      <el-table
          v-loading="loading"
          :data="labels"
          style="width: 100%">
        <el-table-column
            label="Tracking Number"
            prop="tracking">
          <template slot-scope="scope">
            <span v-if="scope.row.tracking_link">
              <a
                  :href="scope.row.tracking_link"
                  target="_blank">{{ scope.row.tracking }}
              </a>
            </span>
            <span v-else>{{ scope.row.tracking }}</span>
          </template>
        </el-table-column>
        <el-table-column
            label="Created at"
            prop="created_at">
          <template slot-scope="scope">
            <time-zone-formatter :current-time="scope.row.created_at" />
          </template>
        </el-table-column>
        <el-table-column
            label="Expires at"
            prop="expires_at">
          <template slot-scope="scope">
            <time-zone-formatter :current-time="scope.row.expires_at" />
          </template>
        </el-table-column>
      </el-table>
    </div>
    <br>

    <h4 class="expanded-title">
      Logs
    </h4>
    <el-table
        v-loading="loading"
        :data="requestHistory"
        style="width: 100%">
      <el-table-column
          label="Updated"
          prop="update_at"
          width="200">
        <template slot-scope="scope">
          <el-tag
              type="info"
              size="small">
            <time-zone-formatter :current-time="scope.row.updated_at" />
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column
          label="State"
          prop="new_state"
          width="200">
        <template slot-scope="scope">
          <el-tag
              :type="scope.$index ? 'info' : ''"
              effect="dark"
              size="small">
            {{ scope.row.new_state | capitalize }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column
          label="Comment"
          prop="comment" />
      <el-table-column
          label="Agent"
          prop="agent" />
    </el-table>
  </div>
</template>

<script>
import { orderBy, findIndex, get, flatten, isArray } from "lodash";
import CustomerRequestCopyData from "@/containers/CustomerRequest/CustomerRequestComponents/CustomerRequestCopy";
import { createNamespacedHelpers as h } from "vuex";
import permission from "@/mixins/permission";
const { mapActions: requestActions, mapGetters: requestGetters } =
  h("db/requests");
const { mapGetters: authGetters } = h("auth");

export default {
  name: "CustomerRequestLogs",
  components: { CustomerRequestCopyData },
  mixins: [permission],
  props: {
    request: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      labels: [],
      logs: [],
      shipments: [],
      loading: false,
      customEmail: null,
      visible: false,
      steps: [
        { n: 0, field: "status", value: "pending" },
        { n: 1, field: "fulfilled", value: true },
        { n: 2, field: "tracking_id", value: true },
        { n: 3, field: "status", value: "resolved" },
        { n: 3, field: "status", value: "failed" },
        { n: 3, field: "status", value: "canceled" },
      ],
    };
  },
  computed: {
    ...authGetters(["user"]),
    validEmail() {
      if (this.customEmail)
        return !!this.customEmail.match(
          /^[a-zA-Z0-9.!\#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/
        );
      else return true;
    },
    requestHistory() {
      return orderBy(this.logs, ["id"], ["desc"]);
    },
    isPending() {
      return this.request.status == "pending";
    },
    isMCFOrder() {
      return this.shipments.length > 0 && this.shipments[0].arrival_datetime;
    },
    showShipments() {
      return this.hasPermission("CustomerRequest", "view-shipments");
    },
    isInProcess() {
      // let currentType = this.request.type
      // if (currentType === "replacement")
      //   return ['in_process', 'shipped'].indexOf(this.request.status) >= 0;
      // else if (currentType == "refund")
      //   return this.request.status === 'pending'
      // else
      //   return false;
      return true;
    },
    isReturnLabelSupportedCountry() {
      const supportedCountries = [
        "us", // United States
        "gb", // United Kingdom
        // EU countries
        "at", // Austria
        "be", // Belgium
        "bg", // Bulgaria
        "hr", // Croatia
        "cy", // Cyprus
        "cz", // Czechia
        "dk", // Denmark
        "ee", // Estonia
        "fi", // Finland
        "fr", // France
        "de", // Germany
        "gr", // Greece
        "hu", // Hungary
        "ie", // Ireland
        "it", // Italy
        "lv", // Latvia
        "lt", // Lithuania
        "lu", // Luxembourg
        "mt", // Malta
        "nl", // Netherlands
        "pl", // Poland
        "pt", // Portugal
        "ro", // Romania
        "sk", // Slovakia
        "si", // Slovenia
        "es", // Spain
        "se", // Sweden
      ];
      return supportedCountries.includes(
        this.request.shipping_address.country.toLowerCase()
      );
    },
    enableReturnLabels() {
      let hasReturnLabelPermission = get(
        this.user.acl,
        "CustomerRequest.return-label",
        false
      );
      return (
        hasReturnLabelPermission &&
        this.isReplacementOrRefund &&
        this.request.has_return_label &&
        this.isReturnLabelSupportedCountry
      );
    },
    address() {
      return this.request.shipping_address;
    },
    currentStep() {
      let step = this.steps.find((step) => {
        step.done = this.request[step.field] === step.value;
        if (!step.done) {
          return step;
        }
      });

      return step.n;
    },
    isReplacementOrRefund() {
      return (
        this.request.type == "replacement" || this.request.type == "refund"
      );
    },
    /**
     * Check if manual label generation is allowed (1.5 hours after order creation)
     * @returns {boolean} True if 1.5 hours have passed since order creation
     */
    canGenerateLabel() {
      const createdAt = new Date(this.request.created_at).getTime();
      // Allow if date is invalid (NaN) or falsy (0 from null/empty string)
      if (!createdAt || isNaN(createdAt)) {
        return true;
      }

      const now = Date.now();
      const oneAndHalfHours = 90 * 60 * 1000;

      return now - createdAt >= oneAndHalfHours;
    },
    /**
     * Get tooltip message for disabled generate label button
     * @returns {string} Tooltip message
     */
    generateLabelTooltip() {
      if (this.canGenerateLabel) {
        return "";
      }
      return "Labels can be manually generated after one and a half hours from the creation of the order.";
    },
  },
  created() {
    // this.loadReturnLabels();
    // if (this.request.type !== "refund")
    this.loadReturn();
  },
  methods: {
    ...requestActions([
      "generateReturnLabel",
      "recoverReturnLabel",
      "deleteReturnLabel",
      "fetchReturnLabels",
      "fetchReturn",
    ]),
    loadReturnLabels() {
      this.loading = true;
      this.fetchReturnLabels(this.request.uuid)
        .then((response) => {
          this.labels = response.labels || [];
        })
        .catch((error) => {
          this.$notify({
            type: "error",
            message: "Fetch Return Labels: " + error.data.error,
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    loadReturn() {
      this.loading = true;
      this.fetchReturn(this.request.uuid)
        .then((response) => {
          this.logs = response.logs;
          this.labels = response.labels;
          this.shipments = response.shipments || [];
          var item_description = null;
          if (response.request.type === "Part") {
            if (response.request.reasons.length > 0)
              item_description = response.request.reasons[0].text;
          } else {
            item_description = response.request.new_device_kind;
          }
          this.shipments.forEach(
            (shipment) => (shipment.item_description = item_description)
          );
        })
        .catch((error) => {
          this.$notify({
            type: "error",
            message: "Fetch Return Logs: " + error.data.error,
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    generateLabel() {
      this.loading = true;
      this.generateReturnLabel({
        uuid: this.request.uuid,
        customEmail: this.customEmail,
      })
        .then((response) => {
          if (response.label) this.labels.push(response.label);
          if (response.error) this.$notify.error(response.error);
        })
        .catch((resp) => {
          console.log(resp);
          let message = null;
          let errors =
            get(resp, "data.body.errors.base") ||
            get(resp, "data.body.base") ||
            get(resp, "data.body.message");
          if (isArray(errors)) {
            errors = flatten(errors);
            let error = errors[0];
            message = error.message || error;
          }
          message = message || errors || "Error generating label";

          this.$notify({
            type: "error",
            message: message,
            duration: 0,
          });
        })
        .finally(() => {
          this.loading = false;
          this.visible = false;
          this.customEmail = null;
        });
    },
    recoverLabel(label) {
      this.loading = true;
      this.recoverReturnLabel({ uuid: this.request.uuid, label_id: label.id })
        .then((response) => {
          let index = findIndex(this.labels, { id: label.id });
          this.labels.splice(index, 1, response.label);
        })
        .catch((error) => {
          this.$notify({
            type: "error",
            message: error.data,
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    deleteLabel(label) {
      this.loading = true;
      this.deleteReturnLabel({ uuid: this.request.uuid, label_id: label.id })
        .then((response) => {
          let index = findIndex(this.labels, { id: label.id });
          this.labels.splice(index, 1, response.label);
        })
        .catch((error) => {
          this.$notify({
            type: "error",
            message: error.data,
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    copyData() {
      this.$refs.copyRequestModal.openModal();
    },
    isCanceled(label) {
      return !!label.canceled_at;
    },
    isExpired(label) {
      let now = new Date().getTime();
      let expiration = new Date(label.expires_at).getTime();
      return expiration < now;
    },
    setStatus(log) {
      if (log.new_state.toLowerCase() == "canceled") {
        return "error";
      } else {
        let index = this.request.logs.indexOf(log);
        return index == this.request.logs.length - 1 ? "wait" : "success";
      }
    },
    isKustomerURL(url) {
      const isValidHttpUrl = (url_to_test) => {
        try {
          const url = new URL(url_to_test);
          return url.protocol === "http:" || url.protocol === "https:";
        } catch (_) {
          return false;
        }
      };

      if (!isValidHttpUrl(url)) return false;
      return !!url.match(
        "https://[\-a-zA-Z0-9]*ring[\-a-zA-Z0-9]*.kustomerapp.com/.*"
      );
    },
  },
};
</script>

<style lang="scss">
.request-info {
  padding: 10px 10px 10px 50px;
  background: #fdfdfd;
  h4.expanded-title {
    padding: 10px;
    margin: 0px;
    background: #f1f1f1;
  }
  .el-steps.el-steps--horizontal {
    background-color: transparent !important;
    margin-bottom: 20px;
  }
  .customer-request-details div {
    margin-bottom: 5px;
  }
  .el-table {
    background-color: transparent !important;
    td,
    th,
    tr {
      background-color: transparent;
    }
  }
  .request-address-row {
    padding: 9px 0px;
    border-bottom: dashed 1px #eee;
    display: flex;
    justify-content: space-between;
  }
  h4.flex {
    display: flex !important;
    justify-content: space-between;
    align-items: center;
  }
}
</style>
